import React, { useState, useEffect } from 'react';
import { Plus, Play, Edit, Trash2, Lock, Globe, Film, X, User, Flame } from 'lucide-react';
import apiService from '../services/apiService';
import PlaylistDetail from './PlaylistDetail';

const MyPlaylists = ({ user, onClose, onPlayMovie }) => {
    const [activeTab, setActiveTab] = useState('mine'); // 'mine' ou 'public'
    const [myPlaylists, setMyPlaylists] = useState([]);
    const [publicPlaylists, setPublicPlaylists] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
    const [selectedPlaylistId, setSelectedPlaylistId] = useState(null);

    useEffect(() => {
        if (user) {
            loadMyPlaylists();
            loadPublicPlaylists();
        }
    }, [user]);

    const loadMyPlaylists = async () => {
        try {
            setLoading(true);
            const response = await apiService.getUserPlaylists();

            if (response.success) {
                setMyPlaylists(response.data);
            }
        } catch (err) {
            console.error('Error loading my playlists:', err);
            setError('Impossible de charger tes playlists');
        } finally {
            setLoading(false);
        }
    };

    const loadPublicPlaylists = async () => {
        try {
            const response = await apiService.getPublicPlaylists();

            if (response.success) {
                setPublicPlaylists(response.data);
            }
        } catch (err) {
            console.error('Error loading public playlists:', err);
        }
    };

    const handleDeletePlaylist = async (playlistId) => {
        try {
            const response = await apiService.deletePlaylist(playlistId);

            if (response.success) {
                setMyPlaylists(myPlaylists.filter(p => p.id !== playlistId));
                setShowDeleteConfirm(null);
            }
        } catch (err) {
            console.error('Error deleting playlist:', err);
            alert('Erreur lors de la suppression');
        }
    };

    const formatDuration = (minutes) => {
        if (minutes < 60) return `${minutes}min`;
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
    };

    // Si une playlist est sélectionnée, afficher le détail
    if (selectedPlaylistId) {
        return (
            <PlaylistDetail
                playlistId={selectedPlaylistId}
                onClose={onClose}
                onBack={() => setSelectedPlaylistId(null)}
                onPlayMovie={onPlayMovie}
            />
        );
    }

    if (!user) {
        return null;
    }

    const currentPlaylists = activeTab === 'mine' ? myPlaylists : publicPlaylists;

    const inputStyle = {
        width: '100%', background: 'var(--glass)', border: '1px solid var(--glass-border)',
        borderRadius: 'var(--r-md)', padding: '10px 14px', color: 'var(--text-1)',
        fontSize: '0.9375rem', outline: 'none', transition: 'border-color 200ms ease, box-shadow 200ms ease',
        boxSizing: 'border-box',
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(12px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div style={{
                background: 'var(--bg-modal)',
                border: '1px solid var(--glass-border)',
                borderRadius: 'var(--r-2xl)',
                boxShadow: 'var(--shadow-xl)',
                width: '100%', maxWidth: 960,
                maxHeight: '90vh',
                overflow: 'hidden',
                display: 'flex', flexDirection: 'column',
                animation: 'scaleIn 0.2s ease-out',
            }}>
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid var(--glass-border)', flexShrink: 0 }}>
                    <div>
                        <h2 style={{ color: 'var(--text-1)', fontWeight: 800, fontSize: '1.375rem' }}>Collections</h2>
                        <p className="hidden sm:block" style={{ color: 'var(--text-4)', fontSize: '0.875rem', marginTop: 4 }}>
                            {activeTab === 'mine' ? 'Organise tes films en playlists' : 'Découvre les playlists des autres'}
                        </p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {activeTab === 'mine' && (
                            <button
                                onClick={() => setShowCreateModal(true)}
                                className="btn-primary"
                                style={{ padding: '8px 14px', fontSize: '0.875rem', gap: 6 }}
                            >
                                <Plus style={{ width: 15, height: 15 }} />
                                <span className="hidden sm:inline">Créer</span>
                            </button>
                        )}
                        <button
                            onClick={onClose}
                            style={{
                                width: 36, height: 36, borderRadius: 'var(--r-md)',
                                background: 'transparent', border: 'none',
                                color: 'var(--text-4)', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                transition: 'background 150ms ease, color 150ms ease',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.background = 'var(--glass-hover)'; e.currentTarget.style.color = 'var(--text-1)'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-4)'; }}
                        >
                            <X style={{ width: 20, height: 20 }} />
                        </button>
                    </div>
                </div>

                {/* Tabs */}
                <div style={{ display: 'flex', borderBottom: '1px solid var(--glass-border)', padding: '0 20px', overflowX: 'auto', flexShrink: 0 }}>
                    {[
                        { key: 'mine', icon: <Lock style={{ width: 14, height: 14 }} />, label: 'Mes playlists', count: myPlaylists.length, countColor: 'var(--accent)' },
                        { key: 'public', icon: <Globe style={{ width: 14, height: 14 }} />, label: 'Publiques', count: publicPlaylists.length, countColor: '#22C55E' },
                    ].map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => setActiveTab(tab.key)}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 6,
                                padding: '12px 16px', whiteSpace: 'nowrap',
                                background: 'transparent', border: 'none',
                                borderBottom: activeTab === tab.key ? '2px solid var(--accent)' : '2px solid transparent',
                                color: activeTab === tab.key ? 'var(--accent-bright)' : 'var(--text-4)',
                                cursor: 'pointer', fontWeight: 600, fontSize: '0.9375rem',
                                transition: 'color 150ms ease, border-color 150ms ease',
                            }}
                        >
                            {tab.icon}
                            {tab.label}
                            {tab.count > 0 && (
                                <span style={{
                                    background: tab.countColor, color: '#fff',
                                    fontSize: '0.6875rem', fontWeight: 700,
                                    padding: '1px 7px', borderRadius: 'var(--r-full)',
                                    opacity: 0.9,
                                }}>{tab.count}</span>
                            )}
                        </button>
                    ))}
                </div>

                {/* Content */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
                    {loading && activeTab === 'mine' ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}>
                            <div style={{ width: 40, height: 40, borderRadius: '50%', border: '3px solid var(--glass-border)', borderTopColor: 'var(--accent)', animation: 'spinSlow 0.8s linear infinite' }} />
                        </div>
                    ) : error && activeTab === 'mine' ? (
                        <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-4)' }}>{error}</div>
                    ) : currentPlaylists.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '80px 0' }}>
                            <Film style={{ width: 56, height: 56, color: 'var(--text-4)', margin: '0 auto 16px', opacity: 0.4 }} />
                            <p style={{ color: 'var(--text-2)', fontSize: '1.125rem', fontWeight: 600, marginBottom: 8 }}>
                                {activeTab === 'mine' ? 'Aucune collection' : 'Aucune playlist publique'}
                            </p>
                            <p style={{ color: 'var(--text-4)', fontSize: '0.875rem', marginBottom: 24 }}>
                                {activeTab === 'mine'
                                    ? 'Crée ta première playlist pour organiser tes films'
                                    : "Les autres utilisateurs n'ont pas encore créé de playlists publiques"
                                }
                            </p>
                            {activeTab === 'mine' && (
                                <button onClick={() => setShowCreateModal(true)} className="btn-primary" style={{ padding: '10px 20px', gap: 8 }}>
                                    <Plus style={{ width: 16, height: 16 }} />
                                    Créer ma première playlist
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3" style={{ gap: 16 }}>
                            {currentPlaylists.map((playlist) => (
                                <div
                                    key={playlist.id}
                                    style={{
                                        background: 'var(--glass)',
                                        border: '1px solid var(--glass-border)',
                                        borderRadius: 'var(--r-xl)',
                                        overflow: 'hidden',
                                        transition: 'border-color 200ms ease, background 200ms ease',
                                    }}
                                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--glass-border-hover)'; e.currentTarget.style.background = 'var(--glass-hover)'; }}
                                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--glass-border)'; e.currentTarget.style.background = 'var(--glass)'; }}
                                >
                                    <div style={{ padding: '16px 16px 12px', borderBottom: '1px solid var(--glass-border)' }}>
                                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 6 }}>
                                            <h3 style={{ color: 'var(--text-1)', fontWeight: 700, fontSize: '1.0625rem', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {playlist.name}
                                            </h3>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 8, flexShrink: 0 }}>
                                                {playlist.is_public
                                                    ? <Globe style={{ width: 14, height: 14, color: '#22C55E' }} />
                                                    : <Lock style={{ width: 14, height: 14, color: 'var(--text-4)' }} />
                                                }
                                                {playlist.movie_count >= 5 && <Flame style={{ width: 14, height: 14, color: '#F97316' }} />}
                                            </div>
                                        </div>
                                        {playlist.description && (
                                            <p style={{ color: 'var(--text-4)', fontSize: '0.875rem', lineHeight: 1.4, marginBottom: 6, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                                                {playlist.description}
                                            </p>
                                        )}
                                        {activeTab === 'public' && playlist.username && (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--text-4)', fontSize: '0.75rem', marginTop: 4 }}>
                                                <User style={{ width: 11, height: 11 }} />
                                                <span>par {playlist.username}</span>
                                            </div>
                                        )}
                                    </div>

                                    <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                                        {[
                                            { label: 'Films', value: playlist.movie_count },
                                            ...(playlist.total_duration > 0 ? [{ label: 'Durée', value: formatDuration(playlist.total_duration) }] : []),
                                        ].map(row => (
                                            <div key={row.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.875rem' }}>
                                                <span style={{ color: 'var(--text-4)' }}>{row.label}</span>
                                                <span style={{ color: 'var(--text-1)', fontWeight: 600 }}>{row.value}</span>
                                            </div>
                                        ))}
                                    </div>

                                    <div style={{ padding: '0 16px 16px', display: 'flex', gap: 8 }}>
                                        <button
                                            onClick={() => setSelectedPlaylistId(playlist.id)}
                                            className="btn-primary"
                                            style={{ flex: 1, padding: '8px', fontSize: '0.875rem', gap: 6 }}
                                        >
                                            <Play style={{ width: 14, height: 14 }} />
                                            Voir
                                        </button>
                                        {activeTab === 'mine' && (
                                            <>
                                                <button
                                                    onClick={() => {/* TODO: edit modal */}}
                                                    className="btn-ghost"
                                                    style={{ width: 36, height: 36, padding: 0 }}
                                                >
                                                    <Edit style={{ width: 14, height: 14 }} />
                                                </button>
                                                <button
                                                    onClick={() => setShowDeleteConfirm(playlist.id)}
                                                    style={{
                                                        width: 36, height: 36, borderRadius: 'var(--r-md)',
                                                        background: 'rgba(239,68,68,0.08)',
                                                        border: '1px solid rgba(239,68,68,0.2)',
                                                        color: '#EF4444', cursor: 'pointer',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        transition: 'background 150ms ease',
                                                    }}
                                                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.16)'}
                                                    onMouseLeave={e => e.currentTarget.style.background = 'rgba(239,68,68,0.08)'}
                                                >
                                                    <Trash2 style={{ width: 14, height: 14 }} />
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Create modal */}
            {showCreateModal && (
                <CreatePlaylistModal
                    onClose={() => setShowCreateModal(false)}
                    onCreated={() => {
                        setShowCreateModal(false);
                        loadMyPlaylists();
                        loadPublicPlaylists();
                    }}
                    inputStyle={inputStyle}
                />
            )}

            {/* Delete confirm */}
            {showDeleteConfirm && (
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
                    <div style={{ background: 'var(--bg-modal)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 'var(--r-xl)', padding: 24, maxWidth: 360, width: '100%', boxShadow: 'var(--shadow-xl)' }}>
                        <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(239,68,68,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                            <Trash2 style={{ width: 22, height: 22, color: '#EF4444' }} />
                        </div>
                        <h3 style={{ color: 'var(--text-1)', fontWeight: 700, fontSize: '1.125rem', textAlign: 'center', marginBottom: 8 }}>Supprimer la playlist ?</h3>
                        <p style={{ color: 'var(--text-4)', fontSize: '0.875rem', textAlign: 'center', marginBottom: 24 }}>
                            Cette action est irréversible. Tous les films seront retirés de la playlist.
                        </p>
                        <div style={{ display: 'flex', gap: 10 }}>
                            <button onClick={() => setShowDeleteConfirm(null)} className="btn-ghost" style={{ flex: 1, padding: '11px' }}>Annuler</button>
                            <button
                                onClick={() => handleDeletePlaylist(showDeleteConfirm)}
                                style={{
                                    flex: 1, padding: '11px', borderRadius: 'var(--r-md)',
                                    background: '#EF4444', border: 'none',
                                    color: '#fff', fontWeight: 700, cursor: 'pointer',
                                    transition: 'background 150ms ease',
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = '#DC2626'}
                                onMouseLeave={e => e.currentTarget.style.background = '#EF4444'}
                            >
                                Supprimer
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const CreatePlaylistModal = ({ onClose, onCreated, inputStyle }) => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [isPublic, setIsPublic] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!name.trim()) { setError('Le nom est obligatoire'); return; }
        try {
            setLoading(true); setError('');
            const response = await apiService.createPlaylist({ name: name.trim(), description: description.trim() || null, is_public: isPublic });
            if (response.success) onCreated();
        } catch (err) {
            setError('Erreur lors de la création');
        } finally { setLoading(false); }
    };

    const focusStyle = (e) => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.boxShadow = '0 0 0 3px var(--accent-glow)'; };
    const blurStyle = (e) => { e.currentTarget.style.borderColor = 'var(--glass-border)'; e.currentTarget.style.boxShadow = 'none'; };

    return (
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div style={{ background: 'var(--bg-modal)', border: '1px solid var(--glass-border)', borderRadius: 'var(--r-xl)', padding: 24, maxWidth: 440, width: '100%', boxShadow: 'var(--shadow-xl)' }}>
                <h3 style={{ color: 'var(--text-1)', fontWeight: 700, fontSize: '1.25rem', marginBottom: 20 }}>Créer une playlist</h3>

                {error && (
                    <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 'var(--r-md)', padding: '10px 14px', marginBottom: 16 }}>
                        <p style={{ color: '#EF4444', fontSize: '0.875rem' }}>{error}</p>
                    </div>
                )}

                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div>
                        <label style={{ display: 'block', color: 'var(--text-3)', fontSize: '0.875rem', fontWeight: 500, marginBottom: 6 }}>Nom de la playlist *</label>
                        <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex: Marathon Marvel"
                            style={inputStyle || { width: '100%', background: 'var(--glass)', border: '1px solid var(--glass-border)', borderRadius: 'var(--r-md)', padding: '10px 14px', color: 'var(--text-1)', fontSize: '0.9375rem', outline: 'none', boxSizing: 'border-box' }}
                            onFocus={focusStyle} onBlur={blurStyle} maxLength={255} />
                    </div>
                    <div>
                        <label style={{ display: 'block', color: 'var(--text-3)', fontSize: '0.875rem', fontWeight: 500, marginBottom: 6 }}>Description (optionnelle)</label>
                        <textarea value={description} onChange={(e) => setDescription(e.target.value)}
                            placeholder="Ex: Tous les films Marvel dans l'ordre chronologique"
                            rows={3}
                            style={{ ...(inputStyle || {}), width: '100%', background: 'var(--glass)', border: '1px solid var(--glass-border)', borderRadius: 'var(--r-md)', padding: '10px 14px', color: 'var(--text-1)', fontSize: '0.9375rem', outline: 'none', resize: 'none', boxSizing: 'border-box' }}
                            onFocus={focusStyle} onBlur={blurStyle} maxLength={1000} />
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'var(--glass)', border: '1px solid var(--glass-border)', borderRadius: 'var(--r-md)', padding: '12px 14px', cursor: 'pointer' }}>
                        <input type="checkbox" id="is_public" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)}
                            style={{ width: 18, height: 18, accentColor: 'var(--accent)', cursor: 'pointer' }} />
                        <div style={{ flex: 1 }}>
                            <span style={{ color: 'var(--text-1)', fontWeight: 600, fontSize: '0.9375rem' }}>Playlist publique</span>
                            <p style={{ color: 'var(--text-4)', fontSize: '0.75rem', marginTop: 2 }}>Les autres utilisateurs pourront voir cette playlist</p>
                        </div>
                        {isPublic ? <Globe style={{ width: 18, height: 18, color: '#22C55E' }} /> : <Lock style={{ width: 18, height: 18, color: 'var(--text-4)' }} />}
                    </label>
                    <div style={{ display: 'flex', gap: 10, paddingTop: 4 }}>
                        <button type="button" onClick={onClose} className="btn-ghost" style={{ flex: 1, padding: '11px' }} disabled={loading}>Annuler</button>
                        <button type="submit" className="btn-primary" style={{ flex: 1, padding: '11px', opacity: loading ? 0.6 : 1 }} disabled={loading}>
                            {loading ? 'Création...' : 'Créer'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default MyPlaylists;